import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import {
    getApiKeys,
    createApiKey,
    revokeApiKey,
    ApiKey,
    CreatedApiKey,
} from "../../services/ApiKeyAdminService";
import styles from "./ApiKeyAdminPage.module.css";

const ApiKeyAdminPage: React.FC = () => {
    const { user, token } = useContext(AuthContext);

    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    const [ownerName, setOwnerName] = useState("");
    const [ownerEmail, setOwnerEmail] = useState("");
    const [appName, setAppName] = useState("");
    const [creating, setCreating] = useState(false);
    const [newKey, setNewKey] = useState<CreatedApiKey | null>(null);

    const isAdmin = user && admins.includes(user.steamID);

    const loadKeys = async () => {
        if (!token) return;
        try {
            setKeys(await getApiKeys(token));
        } catch {
            setStatus({ type: "err", text: "Failed to load API keys." });
        }
        setLoading(false);
    };

    useEffect(() => {
        loadKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleCreate = async () => {
        if (!token || !ownerName.trim()) return;
        setCreating(true);
        setStatus(null);
        setNewKey(null);
        try {
            const created = await createApiKey(
                { ownerName: ownerName.trim(), ownerEmail: ownerEmail.trim() || undefined, appName: appName.trim() || undefined },
                token
            );
            setNewKey(created);
            setOwnerName("");
            setOwnerEmail("");
            setAppName("");
            setStatus({ type: "ok", text: "API key created. Copy it now — it won't be shown again." });
            await loadKeys();
        } catch (err: any) {
            setStatus({ type: "err", text: err?.response?.data || "Failed to create API key." });
        }
        setCreating(false);
    };

    const handleRevoke = async (id: number) => {
        if (!token) return;
        if (!window.confirm("Revoke this API key? The owner will lose access immediately.")) return;
        try {
            await revokeApiKey(id, token);
            setStatus({ type: "ok", text: "API key revoked." });
            await loadKeys();
        } catch (err: any) {
            setStatus({ type: "err", text: err?.response?.data || "Failed to revoke API key." });
        }
    };

    const copy = (text: string) => {
        navigator.clipboard?.writeText(text);
        setStatus({ type: "ok", text: "Copied to clipboard." });
    };

    if (!isAdmin) {
        return (
            <div className={styles.denied}>
                <h2>Access Denied</h2>
                <p>Admin only.</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Public API Keys</h1>
            <p className={styles.subtitle}>
                Issue and revoke keys for the public API (<code>/api/public/v1</code>). Keys are sent by
                consumers in the <code>X-API-Key</code> header. Usage is tracked per key.
            </p>

            {status && (
                <div className={status.type === "ok" ? styles.success : styles.error}>{status.text}</div>
            )}

            <div className={styles.formCard}>
                <h2 className={styles.sectionTitle}>Issue a new key</h2>
                <div className={styles.formRow}>
                    <input
                        className={styles.input}
                        placeholder="Owner name (required) — e.g. Caster Mikkel"
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value)}
                    />
                    <input
                        className={styles.input}
                        placeholder="Owner email (optional)"
                        value={ownerEmail}
                        onChange={(e) => setOwnerEmail(e.target.value)}
                    />
                    <input
                        className={styles.input}
                        placeholder="App / use (optional) — e.g. Stream dashboard"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                    />
                    <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={handleCreate}
                        disabled={creating || !ownerName.trim()}
                    >
                        {creating ? "Creating…" : "Create key"}
                    </button>
                </div>

                {newKey && (
                    <div className={styles.newKeyBox}>
                        <div className={styles.newKeyLabel}>
                            New key for <strong>{newKey.ownerName}</strong> — copy it now, it is shown only once:
                        </div>
                        <div className={styles.newKeyRow}>
                            <code className={styles.newKeyValue}>{newKey.key}</code>
                            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => copy(newKey.key)}>
                                Copy
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <h2 className={styles.sectionTitle}>Existing keys</h2>
            {loading ? (
                <p>Loading…</p>
            ) : keys.length === 0 ? (
                <p className={styles.muted}>No keys issued yet.</p>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Prefix</th>
                                <th>Owner</th>
                                <th>App / use</th>
                                <th>Requests</th>
                                <th>Last used</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => (
                                <tr key={k.id} className={k.active ? "" : styles.revokedRow}>
                                    <td><code>{k.keyPrefix}…</code></td>
                                    <td>
                                        {k.ownerName}
                                        {k.ownerEmail && <div className={styles.muted}>{k.ownerEmail}</div>}
                                    </td>
                                    <td>{k.appName || <span className={styles.muted}>—</span>}</td>
                                    <td>{k.totalRequests.toLocaleString()}</td>
                                    <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : <span className={styles.muted}>never</span>}</td>
                                    <td>
                                        {k.active ? (
                                            <span className={styles.badgeActive}>Active</span>
                                        ) : (
                                            <span className={styles.badgeRevoked}>Revoked</span>
                                        )}
                                    </td>
                                    <td>
                                        {k.active && (
                                            <button
                                                className={`${styles.btn} ${styles.btnDanger}`}
                                                onClick={() => handleRevoke(k.id)}
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ApiKeyAdminPage;
